import mixpanel from 'mixpanel-browser'
import BaseTrackingProvider from './baseTrackingProvider'

/**
 * @typedef {{eventName: string, eventOpts: Object}} eventQueueItem
 * @typedef {{pageName: string, pageViewOpts: Object}} pageViewQueueItem
 */
class MixpanelTrackingProvider extends BaseTrackingProvider {
  static EVENT_SESSION_STARTED = 'session_started'
  sdkLoaded = false
  /**
   * @type eventQueueItem[]
   */
  eventQueue = []
  /**
   * @type pageViewQueueItem[]
   */
  pageViewQueue = []

  setUserId (userId) {
    if (this.userId === userId) {
      return
    }
    super.setUserId(userId)
    if (this.sdkLoaded === true) {
      this.setIdentityOnMixpanelInstance(userId)
    }
  }

  setIdentityOnMixpanelInstance (identity) {
    mixpanel.identify(identity)
    mixpanel.track(MixpanelTrackingProvider.EVENT_SESSION_STARTED, this.getOptsWithDefaults())
    this._processEventQueue()
    this._processPageViewQueue()
  }

  trackEvent (eventName, eventOpts = {}) {
    if (this.sdkLoaded === false) {
      // sdk not loaded -> queue event for when it's ready
      this._addEventToQueue({ eventName, eventOpts })
      return
    }
    if (this.userId === null) {
      // for mixpanel we do not trackEvent until the user has logged in
      return
    }
    mixpanel.track(eventName, this._getOptsWithStringGiataId(this.getOptsWithDefaults(eventOpts)))
  }

  trackPageView (pageName, pageViewOpts = {}) {
    if (this.sdkLoaded === false) {
      // sdk not loaded -> queue event for when it's ready
      this._addPageViewToQueue({ pageName, pageViewOpts })
      return
    }
    if (this.userId === null) {
      // for mixpanel we do not trackEvent until the user has logged in
      return
    }
    mixpanel.track_pageview({
      'Page': pageName,
      ...this._getOptsWithStringGiataId(this.getOptsWithDefaults(pageViewOpts))
    })
  }

  ready () {
    this.sdkLoaded = true
    if (this.userId !== null) {
      this.setIdentityOnMixpanelInstance(this.userId)
    }
  }

  optIn () {
    if (!mixpanel.has_opted_in_tracking()) {
      mixpanel.opt_in_tracking({
        track: null
      })
    }
  }

  optOut () {
    if (mixpanel.has_opted_in_tracking()) {
      mixpanel.opt_out_tracking()
    }
  }

  hasConsented () {
    return window?.CCM?.acceptedEmbeddings?.filter(e => e.name === 'Mixpanel').length > 0
  }

  /**
   * @param eventQueueItem {eventQueueItem}
   */
  _addEventToQueue (eventQueueItem) {
    this.eventQueue.push(eventQueueItem)
  }

  _processEventQueue () {
    for (let i = 0; i < this.eventQueue.length; i++) {
      const evt = this.eventQueue[i]
      this.trackEvent(evt.eventName, evt.eventOpts)
    }
    this.eventQueue = []
  }

  /**
   * @param pageViewQueueItem {pageViewQueueItem}
   */
  _addPageViewToQueue (pageViewQueueItem) {
    this.pageViewQueue.push(pageViewQueueItem)
  }

  _processPageViewQueue () {
    for (let i = 0; i < this.pageViewQueue.length; i++) {
      const pageView = this.pageViewQueue[i]
      this.trackPageView(pageView.pageName, pageView.pageViewOpts)
    }
    this.pageViewQueue = []
  }

  _getOptsWithStringGiataId (opts) {
    if (opts.giataId) {
      opts.giataIdString = opts.giataId.toString()
    }
    return opts
  }
}

const mixpanelTrackingProvider = new MixpanelTrackingProvider()
export default mixpanelTrackingProvider
